import * as React from 'react'

import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

export const useIsContractInBasket = (contractId: string) => {
  const [included, setIncluded] = React.useState(false)

  useEffect(() => {
    const initialIncluded = window.hapi.basket.utils?.getIsProductOrContractInBasket(
      contractId,
      window.hapi.basket.state?.productsMeta?.value
    )

    setIncluded(!!initialIncluded)

    window.hapi.basket.state?.productsMeta.onChange((newValue: BasketProduct[]) => {
      const isIncluded = window.hapi.basket.utils?.getIsProductOrContractInBasket(
        contractId,
        newValue
      )

      setIncluded(!!isIncluded)
    })
  }, [contractId])

  return included
}

export const resetOrderJourney = async () => {
  if (!window.hapi?.orderJourney) return

  await window.hapi.orderJourney.service.restartJourney.run()
  window.hapi.basket.service.clear()
}

export function campaignDurationPeriodLabel(duration: ProductDuration) {
  switch (duration.range) {
    case 'hours':
      return i18n.t('ats.vonq.campaigns_list.range.hours', { amount: duration.period })
    case 'days':
      return i18n.t('ats.vonq.campaigns_list.range.days', { amount: duration.period })
    case 'weeks':
      return i18n.t('ats.vonq.campaigns_list.range.weeks', { amount: duration.period })
    case 'months':
      return i18n.t('ats.vonq.campaigns_list.range.months', { amount: duration.period })
    default:
      duration.period
  }
}

export function campaignStatusLabel(status: CampaignStatus) {
  switch (status) {
    case 'in progress':
      return i18n.t('ats.vonq.campaign_status.in_progress')
    case 'offline':
      return i18n.t('ats.vonq.campaign_status.offline')
    case 'online':
      return i18n.t('ats.vonq.campaign_status.online')
    case 'not processed':
      return i18n.t('ats.vonq.campaign_status.not_processed')
    default:
      ''
  }
}

export interface CampaignWithProducts {
  name: string
  id: string
  status: CampaignStatus
  products: {
    id: string
    name: string
    status: CampaignStatus
    publishedAt: string | null
    durationPeriod: ProductDuration
    visits: number
  }[]
}

export function buildCampaignWithProducts(campaign: Campaign): CampaignWithProducts {
  const products = campaign.orderedProducts.map((productId: string) => {
    const spec = campaign.orderedProductsSpecs.find((spec) => spec.productId === productId)
    const posting = campaign.postings.find((posting) => posting.productId === productId)

    return {
      id: productId,
      name: posting!.name,
      status: spec!.status,
      publishedAt: spec!.deliveredOn,
      durationPeriod: spec!.durationPeriod,
      visits: posting!.clicks,
    }
  })

  return {
    name: campaign.campaignName || '',
    id: campaign.campaignId,
    status: campaign.status,
    products,
  }
}

// -- Types

export type BasketProduct = {
  id: number | string // Product ID or Contract ID
  isProduct: boolean
}

export type ContractProduct = {
  product_id: string
  title: string
}

export type ContractPurchasePrice = {
  amount: number | null
  currency: string | null
}

type ProductBoardType =
  | 'aggregator'
  | 'job board'
  | 'service'
  | 'social media'
  | 'publication'
  | 'community'

type ProductLogo = {
  url: string
  size: string
}

type ContractChannel = {
  logo_rectangle_url?: ProductLogo[] | null
  logo_square_url?: ProductLogo[] | null
  logo_url?: ProductLogo[] | null
  id: number
  type: ProductBoardType
  name: string
  url: string
}

export type Contract = {
  channel?: ContractChannel
  alias: string
  channel_id: number
  class_name: string
  contract_id: string
  credits: number | null
  customer_id: string
  expiry_date: string | null
  product: ContractProduct
  purchase_price: ContractPurchasePrice | null
  posting_duration_days: number | null
  group: ContractGroup | null
}

type ContractGroup = {
  id: string
  idx: number
  igb_customer_id: string
  name: string
}

// -- Procuct

type ProductRange = 'hours' | 'days' | 'weeks' | 'months'

export type ProductDuration = {
  range: ProductRange
  period: number
}

enum ProductPriceCurrency {
  'USD' = 'USD',
  'EUR' = 'EUR',
  'GBP' = 'GBP',
  'AUD' = 'AUD',
}

// -- Order journey
enum OrderJourneyPaymentMethod {
  atsManaged = 'ats_managed',
  wallet = 'wallet',
  purchaseOrder = 'purchase_order',
  directCharge = 'direct_charge',
}

// -- Campaign

export type Campaign = {
  campaignId: string
  labels: Record<string, string> | null
  currency: ProductPriceCurrency
  campaignName: string | null
  companyId: string
  createdOn: string
  poNumber: string | null
  customerId: string
  orderReference: string | null
  orderedProducts: string[]
  orderedProductsSpecs: CampaignProductSpecs[]
  postingDetails: CampaignPostingDetails
  paymentMethod: OrderJourneyPaymentMethod
  postings: CampaignPosting[]
  recruiterInfo: CampaignRecruiterInfo
  status: CampaignStatus
  targetGroup: CampaignTargetGroup
  totalPrice: CampaignTotalPrice
  walletId: string
  detailedStatus?: CampaignDetailedStatus //this is only added virtually on FE on demand
  isEditable: boolean
}

type CampaignProductSpecs = {
  contractId: string | null
  deliveredOn: string | null
  duration: string
  durationPeriod: ProductDuration
  jobBoardLink: string | null
  postingRequirements: Record<string, unknown> | null
  postingRequirementsLabels?: Record<string, string>
  productId: string
  status: CampaignStatus
  utm: string | null
  statusDescription: string | null
}

type CampaignProductStatus = {
  productId: string
  status: CampaignStatus
  statusDescription: string | null
  statusRawError: string | null
  statusSolution: string | null
}

type CampaignDetail = {
  status: CampaignStatus
  orderedProductsStatuses: CampaignProductStatus[]
  campaignId: string
}

type CampaignPostingDetailsContactInfo = {
  emailAddress?: string | null
  name?: string | null
  phoneNumber?: string | null
}

type CampaignPostingDetailsOrganization = {
  companyLogo: string
  name: string
}

type CampaignProductPostingDetailsSalaryIndicationRange = {
  currency?: string | null
  from?: number | null
  to: number
}

type CampaignPostingDetailsSalaryIndication = {
  period: SalaryPeriod
  range: CampaignProductPostingDetailsSalaryIndicationRange
}

type CampaignPostingDetailsWeeklyWorkingHours = {
  to: number
  from?: number | null
}

type CampaignPostingDetailsWorkingLocation = {
  addressLine1: string
  addressLine2: string
  allowsRemoteWork?: boolean | number | null
  city: string
  country: string
  postCode?: string | null
  postcode: string
}

type CampaignPostingDetails = {
  applicationUrl: string
  contactInfo: CampaignPostingDetailsContactInfo
  description: string
  employmentType: EmploymentType
  jobPageUrl: string
  organization: CampaignPostingDetailsOrganization
  salaryIndication: CampaignPostingDetailsSalaryIndication
  title: string
  weeklyWorkingHours: CampaignPostingDetailsWeeklyWorkingHours
  workingLocation: CampaignPostingDetailsWorkingLocation
  yearsOfExperience: number
}

type CampaignPosting = {
  clicks: number
  name: string
  productId: string
}

type CampaignRecruiterInfo = {
  id?: number | null
  name: string
  emailAddress?: string | null
}

type CampaignTargetGroupData = {
  description: string
  vonqId: string | number
}

type CampaignTargetGroup = {
  educationLevel: CampaignTargetGroupData[]
  industry: CampaignTargetGroupData[]
  jobCategory: CampaignTargetGroupData[]
  seniority: CampaignTargetGroupData[]
}

type CampaignTotalPrice = {
  amount: number
  currency: ProductPriceCurrency
}

export type CampaignStatus = 'in progress' | 'offline' | 'online' | 'not processed'

type CampaignDetailsStatusOrderedProductStatus = {
  productId: string
  status: CampaignStatus
  statusDescription: string | null
}

type CampaignDetailedStatus = {
  campaignId: string
  status: CampaignStatus
  orderedProductsStatuses: CampaignDetailsStatusOrderedProductStatus[]
}

enum EmploymentType {
  permanent = 'permanent',
  temporary = 'temporary',
  fixed_term = 'fixed_term',
  fixed_term_with_option_for_permanent = 'fixed_term_with_option_for_permanent',
  freelance = 'freelance',
  traineeship = 'traineeship',
  internship = 'internship',
}

enum SalaryPeriod {
  DAILY = 'daily',
  MONTHLY = 'monthly',
  HOURLY = 'hourly',
  WEEKLY = 'weekly',
  YEARLY = 'yearly',
}
